import React from 'react';
import { clsx } from 'clsx';
import { Check } from 'lucide-react';

export interface Step {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (index: number) => void;
}

export const Stepper: React.FC<StepperProps> = ({
  steps,
  currentStep,
  onStepClick,
}) => {
  return (
    <div className="w-full py-2">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isClickable = onStepClick && isCompleted;

          return (
            <React.Fragment key={step.id}>
              <div
                className={clsx(
                  'flex flex-col items-center flex-1',
                  isClickable && 'cursor-pointer'
                )}
                onClick={() => isClickable && onStepClick(index)}
              >
                <div
                  className={clsx(
                    'w-10 h-10 rounded-xl flex items-center justify-center text-sm font-semibold transition-all duration-200',
                    isCompleted && 'bg-gray-900 text-white',
                    isCurrent && 'bg-gray-900 text-white ring-2 ring-gray-300',
                    !isCompleted && !isCurrent && 'bg-gray-200 text-gray-500'
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <div className="mt-2 text-center">
                  <div
                    className={clsx(
                      'text-xs font-medium',
                      (isCurrent || isCompleted) && 'text-gray-900',
                      !isCurrent && !isCompleted && 'text-gray-500'
                    )}
                  >
                    {step.label}
                  </div>
                  {step.description && (
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      {step.description}
                    </div>
                  )}
                </div>
              </div>

              {index < steps.length - 1 && (
                <div className="flex-1 px-2 mb-4">
                  <div
                    className={clsx(
                      'h-0.5 rounded-full transition-all duration-300',
                      isCompleted ? 'bg-gray-900' : 'bg-gray-200'
                    )}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
